import { useEffect, useState, useRef } from "react";
import { http, API } from "@/lib/api";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { BookmarkSimple } from "@phosphor-icons/react";

export default function ClimateStories() {
  const { user, requireSignIn } = useAuth();
  const [stories, setStories] = useState([]);
  const [bookmarks, setBookmarks] = useState(new Set());
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [topic, setTopic] = useState("");
  const [region, setRegion] = useState("Global");
  const abortRef = useRef(null);

  const load = () => {
    setLoading(true);
    http.get("/stories?limit=12").then(r => setStories(r.data.stories || [])).finally(() => setLoading(false));
    if (user) http.get("/bookmarks").then(r => setBookmarks(new Set((r.data.bookmarks || []).map(b => b.story_id)))).catch(() => {});
  };

  useEffect(() => {
    (async () => {
      await http.post("/stories/seed").catch(() => {});
      load();
    })();
  }, [user]); // eslint-disable-line

  const toggleBookmark = async (story) => {
    if (!requireSignIn("bookmark stories")) return;
    if (bookmarks.has(story.story_id)) {
      try {
        await http.delete(`/bookmarks/${story.story_id}`);
        setBookmarks(s => { const n = new Set(s); n.delete(story.story_id); return n; });
        toast.success("Removed from reading list.");
      } catch { toast.error("Could not remove bookmark."); }
    } else {
      try {
        await http.post("/bookmarks", { story_id: story.story_id });
        setBookmarks(s => new Set(s).add(story.story_id));
        toast.success("Saved to reading list.");
      } catch { toast.error("Could not bookmark."); }
    }
  };

  const streamStory = async (e) => {
    e?.preventDefault?.();
    if (!topic.trim() || streaming) return;
    setStreaming(true);
    setStreamText("");
    setOpen({ streaming: true, topic, region });

    const controller = new AbortController();
    abortRef.current = controller;
    const url = `${API}/stories/stream?topic=${encodeURIComponent(topic)}&region=${encodeURIComponent(region)}`;
    try {
      const res = await fetch(url, { signal: controller.signal, credentials: "include" });
      if (res.status === 429) {
        const body = await res.json().catch(() => ({}));
        toast.error(body.detail || "Rate limit — 5 streams per hour. Try again later.");
        setOpen(null);
        setStreaming(false);
        return;
      }
      if (!res.ok || !res.body) throw new Error("Stream unavailable");
      const reader = res.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const chunk of parts) {
          const lines = chunk.split("\n");
          let event = "message";
          let data = "";
          for (const l of lines) {
            if (l.startsWith("event:")) event = l.slice(6).trim();
            if (l.startsWith("data:")) data += l.slice(5).replace(/^ /, "");
          }
          if (event === "message") setStreamText(t => t + data.replace(/\\n/g, "\n"));
          if (event === "done") {
            toast.success("Story complete.");
            load();
          }
          if (event === "error") toast.error("AI stream failed: " + data);
        }
      }
    } catch (err) {
      if (err.name !== "AbortError") toast.error("Streaming failed. Try again.");
    } finally {
      setStreaming(false);
      setTopic("");
    }
  };

  const closeModal = () => {
    if (streaming && abortRef.current) abortRef.current.abort();
    setOpen(null);
    setStreamText("");
    setStreaming(false);
  };

  return (
    <section data-testid="stories-section" className="py-24 md:py-40 px-6 md:px-10 max-w-[1600px] mx-auto bg-[color:var(--np-bg-2)]">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
        <div className="md:col-span-5">
          <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)] mb-4">/05 · Climate Stories · Live streaming</div>
          <h2 className="font-serif-display text-5xl md:text-7xl leading-[0.95] tracking-tighter">
            Journalism<br/>from the archive.
          </h2>
        </div>
        <div className="md:col-span-6 md:col-start-7 self-end">
          <p className="text-[color:var(--np-text-2)] text-lg mb-6">
            AI-generated editorials grounded in NOAA, NASA, and IPCC data — <em className="text-[color:var(--np-brand)]">now streaming live</em>, one word at a time.
          </p>
          <form onSubmit={streamStory} className="flex flex-col sm:flex-row gap-3" data-testid="story-generate-form">
            <input
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g., How the Sundarbans are disappearing"
              className="flex-1 bg-transparent border border-black/15 focus:border-[color:var(--np-brand)] outline-none px-4 py-3 font-mono-tech text-sm"
              data-testid="story-topic-input"
              disabled={streaming}
            />
            <select
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="bg-[color:var(--np-bg)] border border-black/15 px-4 py-3 font-mono-tech text-xs uppercase tracking-widest"
              data-testid="story-region-select"
              disabled={streaming}
            >
              {["Global","India","Africa","Asia","Europe","North America","South America","Arctic","Oceania"].map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <button
              disabled={streaming}
              className="font-mono-tech text-[11px] tracking-[0.3em] uppercase border border-black/15 hover:bg-[color:var(--np-text)] hover:text-[color:var(--np-bg)] px-6 py-3 transition-colors disabled:opacity-60"
              data-testid="story-generate-btn"
            >{streaming ? "Streaming…" : "Stream →"}</button>
          </form>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10">
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => <div key={i} className="bg-[color:var(--np-bg)] h-64 animate-pulse" />)
        ) : (
          stories.map((s, i) => (
            <motion.button
              key={s.story_id}
              onClick={() => setOpen(s)}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.5, delay: i * 0.03 }}
              className="bg-[color:var(--np-bg)] text-left p-8 min-h-[280px] flex flex-col justify-between group hover:bg-[color:var(--np-bg-2)] transition-colors"
              data-testid={`story-card-${i}`}
            >
              <div>
                <div className="font-mono-tech text-[10px] tracking-[0.35em] uppercase text-[color:var(--np-brand)] mb-4">{s.region}</div>
                <h3 className="font-serif-display text-3xl leading-tight tracking-tight group-hover:text-[color:var(--np-brand)] transition-colors">{s.topic}</h3>
              </div>
              <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-[color:var(--np-muted)] mt-8 flex items-center justify-between">
                <span>{new Date(s.created_at).toISOString().slice(0,10)}</span>
                <span className="opacity-0 group-hover:opacity-100 transition-opacity">Read →</span>
              </div>
            </motion.button>
          ))
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={closeModal}>
          <motion.article
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative max-w-3xl w-full bg-[color:var(--np-bg)] border border-black/15 p-8 md:p-12 my-10"
            onClick={(e) => e.stopPropagation()}
            data-testid="story-modal"
          >
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-text-2)] hover:text-[color:var(--np-text)]"
              data-testid="story-close-btn"
            >Close ✕</button>
            {open.story_id && (
              <button
                onClick={() => toggleBookmark(open)}
                className={`absolute top-4 right-24 flex items-center gap-2 font-mono-tech text-[10px] tracking-[0.3em] uppercase transition-colors ${
                  bookmarks.has(open.story_id) ? "text-[color:var(--np-brand)]" : "text-[color:var(--np-text-2)] hover:text-[color:var(--np-text)]"
                }`}
                data-testid="story-bookmark-btn"
              >
                <BookmarkSimple size={14} weight={bookmarks.has(open.story_id) ? "fill" : "regular"} />
                {bookmarks.has(open.story_id) ? "Saved" : "Save"}
              </button>
            )}
            <div className="font-mono-tech text-[10px] tracking-[0.35em] uppercase text-[color:var(--np-brand)] mb-4">
              {open.region} {open.streaming && <span className="ml-2 text-[color:var(--np-brand)] animate-pulse">● Live</span>}
            </div>
            <h3 className="font-serif-display text-4xl md:text-5xl leading-[1] tracking-tight mb-8">{open.topic}</h3>
            <div className="max-w-none text-[color:var(--np-text)] whitespace-pre-wrap font-body text-base leading-relaxed">
              {open.streaming ? streamText || <span className="text-[color:var(--np-muted)]">Warming up the model…</span> : open.content}
              {streaming && open.streaming && <span className="inline-block w-2 h-5 align-middle ml-1 bg-[color:var(--np-brand)] animate-pulse" />}
            </div>
          </motion.article>
        </div>
      )}
    </section>
  );
}
